import { useCallback, useMemo } from 'react';

interface IOfferPreviewValues {
  offerUiId?: string;
  [key: string]: unknown;
}

interface IUseOfferPreviewDataProps<TBuiltOffer, TOfferData> {
  values: IOfferPreviewValues;
  getOfferUI: { data?: unknown };
  productsListForPreview: unknown[];
  badges: unknown[];
  emblemBadges: unknown[];
  ribbonBadges: unknown[];
  transformPriceValue: () => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildCurrentOffer: (params: any) => TBuiltOffer;
  buildOfferData: (offer: TBuiltOffer) => TOfferData;
}

/**
 * Hook that generates dynamic preview data for any offer type.
 * Accepts a builder function and an offerData wrapper, producing memoized preview data.
 *
 * @example
 * const previewData = useOfferPreviewData({
 *   values,
 *   getOfferUI,
 *   productsListForPreview,
 *   badges,
 *   emblemBadges,
 *   ribbonBadges,
 *   transformPriceValue,
 *   buildCurrentOffer: buildCurrentBundle,
 *   buildOfferData
 * });
 */
export const useOfferPreviewData = <TBuiltOffer, TOfferData>({
  values,
  getOfferUI,
  productsListForPreview,
  badges,
  emblemBadges,
  ribbonBadges,
  transformPriceValue,
  buildCurrentOffer,
  buildOfferData
}: IUseOfferPreviewDataProps<TBuiltOffer, TOfferData>): TOfferData | null => {
  const stableTransformPriceValue = useCallback(() => {
    return transformPriceValue();
  }, [transformPriceValue]);

  const dynamicOfferData = useMemo((): TOfferData | null => {
    if (!values.offerUiId || !getOfferUI.data) {
      return null;
    }

    const currentOffer = buildCurrentOffer({
      values,
      getOfferUI: getOfferUI.data,
      productsListForPreview,
      badges,
      emblemBadges,
      ribbonBadges,
      transformPriceValue: stableTransformPriceValue
    });

    return buildOfferData(currentOffer);
  }, [
    values,
    getOfferUI.data,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    stableTransformPriceValue,
    buildCurrentOffer,
    buildOfferData
  ]);

  return dynamicOfferData;
};
